import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { getProjects, createProject, updateProject, deleteProject, getSubProjects } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';
import Select from '../components/Select';
import { Pencil, Trash2, FolderOpen, Plus, ExternalLink } from 'lucide-react';

const SAP_MODULES = ['FI', 'CO', 'MM', 'SD', 'PP', 'HR', 'PM', 'QM', 'BASIS', 'ABAP', 'Other'];
const EMPTY_FORM  = { name: '', description: '', project_type: 'non_sap', sap_module: '', status: 'active',
                      company_name: '', contact_person: '', contact_email: '', contact_phone: '', address: '' };

const STATUS_STYLE = {
  active:    { bg: '#d1fae5', color: '#065f46' },
  inactive:  { bg: '#f3f4f6', color: '#6b7280' },
  completed: { bg: '#e0f2fe', color: '#0369a1' },
  on_hold:   { bg: '#fef3c7', color: '#92400e' },
};

export default function ProjectsPage() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const isAdmin    = user?.role === 'admin';

  const [projects,   setProjects]   = useState([]);
  const [subProjMap, setSubProjMap] = useState({});
  const [loading,    setLoading]    = useState(true);

  // Modal
  const [modal,   setModal]   = useState(null);   // null | 'create' | 'edit'
  const [editing, setEditing] = useState(null);   // client being edited
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [formErr, setFormErr] = useState('');

  /* ── Fetch ── */
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, spRes] = await Promise.all([getProjects(), getSubProjects()]);
      setProjects(pRes.data || []);
      const map = {};
      (spRes.data || []).forEach(sp => { map[sp.project_id] = (map[sp.project_id] || 0) + 1; });
      setSubProjMap(map);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  /* ── Open modals ── */
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setFormErr('');
    setModal('create');
  };

  const openEdit = (e, p) => {
    e.stopPropagation(); // don't navigate to tickets
    setForm({
      name:           p.name           || '',
      description:    p.description    || '',
      project_type:   p.project_type   || 'non_sap',
      sap_module:     p.sap_module     || '',
      status:         p.status         || 'active',
      company_name:   p.company_name   || '',
      contact_person: p.contact_person || '',
      contact_email:  p.contact_email  || '',
      contact_phone:  p.contact_phone  || '',
      address:        p.address        || '',
    });
    setEditing(p);
    setFormErr('');
    setModal('edit');
  };

  const closeModal = () => { setModal(null); setEditing(null); setForm(EMPTY_FORM); setFormErr(''); };

  /* ── Save (create or update) ── */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormErr('Client name is required.'); return; }
    setSaving(true); setFormErr('');
    try {
      if (modal === 'create') {
        await createProject(form);
      } else {
        await updateProject(editing.id, form);
      }
      closeModal();
      fetchAll();
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Failed to save client.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (e, p) => {
    e.stopPropagation();
    const res = await Swal.fire({
      title: 'Delete Client?',
      html: `<b>${p.name}</b> and all its projects will be permanently deleted.<br/><br/>Existing tickets will be <b>unlinked</b> (not deleted).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });
    if (!res.isConfirmed) return;
    try {
      await deleteProject(p.id);
      fetchAll();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Failed to delete client.', 'error');
    }
  };

  /* ── Render ── */
  return (
    <AppLayout pageTitle="Clients">

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
          {projects.length} client{projects.length !== 1 ? 's' : ''}
        </p>
        {isAdmin && (
          <button
            onClick={openCreate}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
          >
            <Plus size={15} /> New Client
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <div className="spinner" style={{ width: 32, height: 32 }} />
        </div>
      ) : projects.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
          <FolderOpen size={40} style={{ opacity: 0.25 }} />
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>No clients yet.</p>
          {isAdmin && (
            <button onClick={openCreate} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Create first client
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 16 }}>
          {projects.map(p => {
            const ss = STATUS_STYLE[p.status] || STATUS_STYLE.active;
            return (
              <div
                key={p.id}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', transition: 'box-shadow 0.15s', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
                onClick={() => navigate(`/tickets?project_id=${p.id}&project_name=${encodeURIComponent(p.name)}`)}
              >
                {/* Card header strip */}
                <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid var(--border-light)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{
                      background: p.project_type === 'sap' ? 'var(--primary-light)' : '#d1fae5',
                      color: p.project_type === 'sap' ? 'var(--primary)' : '#065f46',
                      padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    }}>
                      {p.project_type === 'sap' ? '🔷 SAP' : '🟢 Non-SAP'}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: ss.bg, color: ss.color, textTransform: 'capitalize' }}>
                      {p.status || 'active'}
                    </span>
                  </div>

                  <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.3 }}>{p.name}</h3>
                  {p.company_name && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-sub)', fontWeight: 600, marginBottom: 2 }}>🏢 {p.company_name}</div>
                  )}
                  {p.contact_person && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>👤 {p.contact_person}</div>
                  )}
                  {(p.contact_email || p.contact_phone) && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
                      {p.contact_email && <span>✉️ {p.contact_email}</span>}
                      {p.contact_email && p.contact_phone && <span>  ·  </span>}
                      {p.contact_phone && <span>📞 {p.contact_phone}</span>}
                    </div>
                  )}
                  {p.sap_module && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Module: {p.sap_module}</div>
                  )}
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, minHeight: 34,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {p.description || 'No description provided.'}
                  </p>
                </div>

                {/* Stats */}
                <div style={{ padding: '10px 16px', display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-muted)' }}>
                  <span>🎫 {p.open_tickets || 0} open</span>
                  {p.member_count > 0 && <span>👥 {p.member_count}</span>}
                  {subProjMap[p.id] > 0 && <span>📂 {subProjMap[p.id]} project{subProjMap[p.id] !== 1 ? 's' : ''}</span>}
                  {p.created_by_name && <span style={{ marginLeft: 'auto', fontSize: 11 }}>by {p.created_by_name}</span>}
                </div>

                {/* Admin action bar */}
                {isAdmin && (
                  <div
                    style={{ display: 'flex', borderTop: '1px solid var(--border-light)' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => navigate(`/tickets?project_id=${p.id}&project_name=${encodeURIComponent(p.name)}`)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 0', border: 'none', borderRight: '1px solid var(--border-light)', background: 'transparent', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--primary)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                      <ExternalLink size={12} /> View Tickets
                    </button>
                    <button
                      onClick={e => openEdit(e, p)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 0', border: 'none', borderRight: '1px solid var(--border-light)', background: 'transparent', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-light)'; e.currentTarget.style.color = 'var(--primary)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={e => handleDelete(e, p)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 0', border: 'none', background: 'transparent', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? '➕ New Client' : '✏️ Edit Client'}</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Client Name <span className="required">*</span></label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. ACME Corp"
                    required
                    autoFocus
                  />
                </div>

                {/* ── Client details ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Company</label>
                    <input
                      className="form-control"
                      value={form.company_name}
                      onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                      placeholder="Legal / company name"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Contact Person</label>
                    <input
                      className="form-control"
                      value={form.contact_person}
                      onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))}
                      placeholder="e.g. John Doe"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Contact Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.contact_email}
                      onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                      placeholder="name@company.com"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Contact Phone</label>
                    <input
                      className="form-control"
                      value={form.contact_phone}
                      onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                      placeholder="e.g. +91 98765 43210"
                    />
                  </div>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Address</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Client address..."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Type <span className="required">*</span></label>
                    <Select
                      value={form.project_type}
                      onChange={v => setForm(f => ({ ...f, project_type: v, sap_module: '' }))}
                      options={[{ value: 'non_sap', label: 'Non-SAP' }, { value: 'sap', label: 'SAP' }]}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Status</label>
                    <Select
                      value={form.status}
                      onChange={v => setForm(f => ({ ...f, status: v }))}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'on_hold', label: 'On Hold' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'inactive', label: 'Inactive' },
                      ]}
                    />
                  </div>
                </div>

                {form.project_type === 'sap' && (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">SAP Module</label>
                    <Select
                      value={form.sap_module}
                      onChange={v => setForm(f => ({ ...f, sap_module: v }))}
                      options={[{ value: '', label: '— Select module —' }, ...SAP_MODULES.map(m => ({ value: m, label: m }))]}
                    />
                  </div>
                )}

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Brief description of this client..."
                  />
                </div>

                {formErr && (
                  <div style={{ padding: '9px 13px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 13 }}>
                    ⚠️ {formErr}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !form.name.trim()}>
                  {saving ? 'Saving…' : modal === 'create' ? 'Create Client' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
